import { freshnessDate } from "@/lib/currentYear";
import { FreshnessNote } from "@/components/FreshnessNote";
import { useCallback, useMemo, useState, lazy, Suspense } from "react";
import { useNavigate } from "@/lib/router-compat";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHelmet } from "@/components/SEOHelmet";
import { CardSkeleton } from "@/components/CardSkeleton";
import { PopularCardsSection } from "@/components/PopularCardsSection";
import { SegmentLinks } from "@/components/SegmentLinks";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useCards } from "@/hooks/useCards";
import { useOffers } from "@/hooks/useOffers";
import { useAdPlacements } from "@/hooks/useAdPlacements";
import { normalizeDbCard, toListCardVM } from "@/lib/cardCoreNormalizer";
import { getSiteOrigin } from "@/lib/ssr-safe";
import { SEGMENT_SHORT_ANSWERS, type SegmentPageConfig } from "@/config/segmentPages";
import { QuickAnswer } from "@/components/QuickAnswer";
import type { Database } from "@/integrations/supabase/types";

const CardCard = lazy(() => import("@/components/CardCard").then((m) => ({ default: m.CardCard })));

type CardRow = Database["public"]["Tables"]["cards"]["Row"];

interface SegmentLandingProps {
  config: SegmentPageConfig;
}

export default function SegmentLanding({ config }: SegmentLandingProps) {
  const navigate = useNavigate();
  const { data: cards = [], isLoading } = useCards();
  const { data: offers = [] } = useOffers();
  const { data: adPlacements = [] } = useAdPlacements();
  const [activeChips, setActiveChips] = useState<string[]>([]);

  const domain = getSiteOrigin();
  const pageUrl = `${domain}/${config.path}`;

  const sponsoredCardIds = useMemo(
    () => new Set(adPlacements.map((ad) => ad.card_id)),
    [adPlacements],
  );

  const sponsoredLabels = useMemo(() => {
    const map = new Map<string, string>();
    adPlacements.forEach((ad) => {
      if (ad.card_id) map.set(ad.card_id, ad.label || "Anzeige");
    });
    return map;
  }, [adPlacements]);

  const offersMap = useMemo(() => {
    const map = new Map<string, any>();
    offers.forEach((offer) => {
      if (offer.card_id) map.set(offer.card_id, offer);
    });
    return map;
  }, [offers]);

  const segmentCards = useMemo(
    () => (cards as CardRow[]).filter((card) => config.baseFilter(card)),
    [cards, config],
  );

  const visibleCards = useMemo(() => {
    const active = config.chips.filter((chip) => activeChips.includes(chip.id));
    const filtered = segmentCards.filter((card) => active.every((chip) => chip.predicate(card)));

    return [...filtered].sort((a, b) => {
      const aSponsored = sponsoredCardIds.has(a.id);
      const bSponsored = sponsoredCardIds.has(b.id);
      if (aSponsored !== bSponsored) return aSponsored ? -1 : 1;
      return (b.boost_priority || 0) - (a.boost_priority || 0);
    });
  }, [segmentCards, activeChips, config, sponsoredCardIds]);

  const chipCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    config.chips.forEach((chip) => {
      counts[chip.id] = segmentCards.filter((card) => chip.predicate(card)).length;
    });
    return counts;
  }, [segmentCards, config]);

  const tiles = useMemo(
    () =>
      visibleCards.map((card) => {
        const normalized = { ...card, ...normalizeDbCard(card), id: String(card.id) };
        return { card, tile: toListCardVM(normalized, offersMap.get(card.id)) };
      }),
    [visibleCards, offersMap],
  );

  const schemas = useMemo(() => {
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: config.h1,
      itemListElement: visibleCards.slice(0, 20).map((card, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${domain}/kreditkarten/${card.slug}`,
        name: card.name,
      })),
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Startseite", item: domain },
        { "@type": "ListItem", position: 2, name: config.h1, item: pageUrl },
      ],
    };

    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: config.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    };

    return [itemList, breadcrumb, faq];
  }, [config, visibleCards, domain, pageUrl]);

  const handleNavigate = useCallback(
    (page: string) => {
      if (page === "home") navigate("/");
    },
    [navigate],
  );

  const handleViewDetails = useCallback(
    (slug: string) => {
      navigate(`/kreditkarten/${slug}`);
    },
    [navigate],
  );

  const toggleChip = useCallback((id: string) => {
    setActiveChips((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      <SEOHelmet
        title={config.seoTitle}
        description={config.seoDescription}
        url={pageUrl}
        type="website"
        modifiedTime={freshnessDate()}
        additionalSchemas={schemas}
      />

      <Header onNavigate={handleNavigate} currentPage={config.pageKey} />

      <main className="flex-1">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 max-w-3xl">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{config.h1}</h1>
            <p className="text-gray-600">{config.intro}</p>
            <FreshnessNote className="mt-3" label="Konditionen geprüft" />
            {SEGMENT_SHORT_ANSWERS[config.path] && (
              <QuickAnswer className="mt-5" text={SEGMENT_SHORT_ANSWERS[config.path]} />
            )}
          </div>

          <div className="mb-6">
            <p className="text-sm font-medium text-gray-900 mb-3">
              {isLoading
                ? "Karten werden geladen …"
                : `${visibleCards.length} ${config.countNoun} gefunden`}
            </p>
            <div className="flex flex-wrap gap-2">
              {config.chips.map((chip) => {
                const active = activeChips.includes(chip.id);
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => toggleChip(chip.id)}
                    aria-pressed={active}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-primary/50"
                    }`}
                  >
                    {chip.label}
                    <span className={`ml-2 text-xs ${active ? "opacity-80" : "text-muted-foreground"}`}>
                      {chipCounts[chip.id] ?? 0}
                    </span>
                  </button>
                );
              })}
              {activeChips.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setActiveChips([])}>
                  Filter zurücksetzen
                </Button>
              )}
            </div>
          </div>

          <div className="mt-8">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            ) : tiles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">Keine Karten entsprechen Ihren Filtern</p>
                <Button className="mt-4" onClick={() => setActiveChips([])}>
                  Filter zurücksetzen
                </Button>
              </div>
            ) : (
              <Suspense
                fallback={
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <CardSkeleton key={i} />
                    ))}
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tiles.map(({ card, tile }, index) => (
                    <CardCard
                      key={card.id}
                      tile={tile}
                      rawCard={card}
                      offer={offersMap.get(card.id)}
                      isSponsored={sponsoredCardIds.has(card.id)}
                      sponsoredLabel={sponsoredLabels.get(card.id)}
                      priority={index < 3}
                    />
                  ))}
                </div>
              </Suspense>
            )}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          {config.sections.map((section) => (
            <div key={section.heading} className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="text-gray-700 leading-relaxed mb-3">
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <ul className="list-disc pl-5 space-y-1 text-gray-700">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Häufige Fragen</h2>
            <Accordion type="single" collapsible className="w-full">
              {config.faqs.map((faq, index) => (
                <AccordionItem key={faq.question} value={`faq-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-700">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <SegmentLinks currentPath={config.path} />

        {/* Gängige Kreditkarten – always shown directly above the footer */}
        <PopularCardsSection
          currentPage={config.pageKey}
          forceVisible
          onViewDetails={handleViewDetails}
          onViewAllCards={() => navigate("/kreditkarten")}
        />
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
}
